import { isValidIBAN, isValidBIC, friendlyFormatIBAN } from "ibantools";

/**
 * IBAN/BIC-Validierung & -Formatierung. WICHTIG: wir strippen ALLE Whitespaces
 * selbst (auch NBSP/Tab/Zeilenumbruch aus Bank-PDFs/Online-Banking) — ibantools'
 * eigenes Stripping erfasst nur Bindestrich/ASCII-Space und würde sonst gültige
 * IBANs fälschlich ablehnen bzw. mit NBSP speichern. Anzeige = 4er-Gruppen.
 */

/** Elektronische Form (alle Whitespaces raus, uppercase). Robust gegen Nicht-Strings. */
function electronic(input: unknown): string {
  return String(input ?? "").replace(/\s+/g, "").toUpperCase();
}

/** Speicher-/Anzeigeform: in 4er-Gruppen. Leer → "". */
export function formatIbanForDisplay(input: string): string {
  const e = electronic(input);
  if (!e) return "";
  return friendlyFormatIBAN(e) ?? e;
}

/** Speicherform (= gruppierte Anzeigeform, gültig + lesbar). */
export function normalizeIbanForStorage(input: string): string {
  return formatIbanForDisplay(input);
}

/** Leer = gültig (optionales Feld); sonst mod-97 (ibantools). */
export function validateIban(input: string): { valid: boolean; error?: string } {
  const e = electronic(input);
  if (!e) return { valid: true };
  return isValidIBAN(e) ? { valid: true } : { valid: false, error: "Ungültige IBAN" };
}

/** BIC: trim, uppercase, alle Whitespaces raus. */
export function normalizeBic(input: string): string {
  return electronic(input);
}

/** Leer = gültig; sonst 8- oder 11-stelliges BIC-Format. */
export function validateBic(input: string): { valid: boolean; error?: string } {
  const bic = electronic(input);
  if (!bic) return { valid: true };
  return isValidBIC(bic) ? { valid: true } : { valid: false, error: "Ungültiger BIC" };
}

// --- Formatierung beim Tippen -------------------------------------------
// Bis 08/2026 wurde die IBAN erst beim Verlassen des Feldes in 4er-Gruppen
// gelegt; während des Tippens stand dort eine Zeichenkette am Stück. Die
// Gruppierung schon beim Tippen zu setzen ist der Punkt, an dem eine Maske
// üblicherweise bricht: der Cursor springt ans Ende, und Backspace an einer
// Gruppengrenze löscht das Leerzeichen, das sofort wieder erscheint — die
// Taste wirkt dann tot. Deshalb ist die Logik hier eine reine Funktion mit
// Tests, und die Komponente reicht nur Cursor rein und raus.

/** Zeichen, die zur IBAN selbst gehören (alles andere ist Trennung). */
const istIbanZeichen = (z: string) => /[A-Z0-9]/.test(z);

/** Wie viele IBAN-Zeichen stehen links von `pos`? */
function zeichenVorCursor(text: string, pos: number): number {
  let n = 0;
  for (let i = 0; i < Math.min(pos, text.length); i++) if (istIbanZeichen(text[i].toUpperCase())) n++;
  return n;
}

/** Umkehrung: hinter welcher Textposition stehen `n` IBAN-Zeichen? */
function cursorNachZeichen(text: string, n: number): number {
  if (n <= 0) return 0;
  let gezaehlt = 0;
  for (let i = 0; i < text.length; i++) {
    if (istIbanZeichen(text[i])) {
      gezaehlt++;
      if (gezaehlt === n) return i + 1;
    }
  }
  return text.length;
}

/**
 * Eine Eingabe in das IBAN-Feld auf die gruppierte Form bringen und den
 * Cursor an der fachlich richtigen Stelle halten.
 *
 * `vorher` ist nötig, um das Löschen eines reinen Trennzeichens zu erkennen:
 * Steht der Cursor hinter einer Gruppengrenze und wird Backspace gedrückt,
 * entfernt der Browser nur das Leerzeichen. Die Formatierung setzt es sofort
 * wieder — für den Anwender passiert nichts. In diesem Fall löschen wir
 * zusätzlich das Zeichen davor, so wie er es erwartet hat.
 */
export function ibanTippen(
  vorher: string,
  nachher: string,
  cursor: number,
): { wert: string; cursor: number } {
  const gross = nachher.toUpperCase();
  let roh = gross.replace(/[^A-Z0-9]/g, "");
  let zeichenLinks = zeichenVorCursor(gross, cursor);

  // Nur ein Trennzeichen gelöscht → die Ziffer davor mitnehmen.
  const nurTrennerWeg =
    roh === electronic(vorher) && nachher.length < vorher.length && zeichenLinks > 0;
  if (nurTrennerWeg) {
    roh = roh.slice(0, zeichenLinks - 1) + roh.slice(zeichenLinks);
    zeichenLinks -= 1;
  }

  const wert = roh ? (friendlyFormatIBAN(roh) ?? roh) : "";
  return { wert, cursor: cursorNachZeichen(wert, zeichenLinks) };
}

/**
 * Bankleitzahl aus einer deutschen IBAN (Stellen 5–12). Für jede andere
 * Länderkennung `undefined` — die Stellenbelegung gilt nur für DE, und ein
 * geratener BIC in einer Bankverbindung ist schlimmer als ein leeres Feld.
 */
export function bankleitzahlAusIban(input: string): string | undefined {
  const e = electronic(input);
  if (!e.startsWith("DE") || e.length < 12) return undefined;
  const blz = e.slice(4, 12);
  return /^\d{8}$/.test(blz) ? blz : undefined;
}
